import {
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { RxIconModule } from '@nrwl/rx-icon';
import { SimpleTable } from './simple-table';
import { SimpleTableCellDef } from './simple-table-cell-def';
import { SimpleTableColumnDef } from './simple-table-column-def';
import { SimpleTableHeaderCellDef } from './simple-table-header-cell-def';
import { RxDialogComponent } from '@nrwl/rx-dialog';
import { AddMedicalNoteDialog } from './add-medical-note-dialog';
import { catchError, filter, forkJoin, map, of, skip, switchMap } from 'rxjs';
import { rxResource, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DialogService } from '@nrwl/dialog-service';
import { DialogsConfigService } from '@nrwl/dialog-configs';
import { DatePipe } from '@angular/common';
import { MedicalNotesService } from './medical-notes-service';
import { LocalMedicalNote } from './local-medical-note';
import { MedicalNoteItem } from './medical-note-item';
import { UserStoreSelector } from '@nrwl/rx-state-user';
import { Store } from '@ngrx/store';
import { AuthService } from '@nrwl/rx-service-auth';
import { Doctor } from '@nrwl/rx-models';
import { parseLocalDateString } from '../../../helpers/date-utils';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';

@Component({
  selector: 'fb-medical-notes-widget-edit',
  imports: [
    SimpleTable,
    SimpleTableColumnDef,
    SimpleTableHeaderCellDef,
    SimpleTableCellDef,
    RxIconModule,
    DatePipe,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  templateUrl: './medical-notes-widget-edit.html',
  styleUrl: './medical-notes-widget-edit.scss',
})
export class MedicalNotesWidgetEdit implements OnInit {
  private readonly injector = inject(Injector);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly dialogsConfigService = inject(DialogsConfigService);
  private readonly medicalNotesService = inject(MedicalNotesService);
  private readonly authService = inject(AuthService);

  /**
   * Previously added medical notes.
   */
  public readonly data = input<readonly MedicalNoteItem[]>([]);
  public readonly markerTargets = input<readonly (ScribeSuggestionMarkerTarget | null)[]>([]);

  /**
   * ID of the appointment.
   */
  public readonly appointmentId = input<string | undefined>();

  /**
   * Patient remote ID.
   */
  public readonly patientRemoteId = input<string | undefined>();

  /**
   * Metadata to be included.
   */
  public readonly metadata = computed(() => {
    const notes = this.notes();

    return {
      medical_notes: notes.map((note) => ({
        id: note['id'],
        date: parseLocalDateString(note.date),
        employee_id: note.employeeId,
        medical_notes: note.text,
      })),
      ...(this.treatment.hasValue() ? { treatment_id: this.treatment.value().id } : {}),
    };
  });

  /**
   * Emits whether the payload should be updated.
   */
  public readonly update = output<readonly MedicalNoteItem[]>();

  /**
   * Currently authenticated user.
   */
  protected readonly user = toSignal(this.store.select(UserStoreSelector.getUser), {
    initialValue: null,
  });

  /**
   * Fallback user if cached one is missing.
   */
  protected readonly fallbackUser = signal<Doctor | null>(null);

  /**
   * Treatment to be used.
   */
  protected readonly treatment = rxResource({
    request: () => {
      const appointmentId = this.appointmentId();

      // Fetch treatment only if appointmentId is defined.
      if (!appointmentId) {
        return undefined;
      }

      return { appointmentId };
    },
    loader: ({ request }) => this.medicalNotesService.getTreatment(request),
  });

  /**
   * Medical notes from PNE.
   */
  protected readonly externalNotes = rxResource({
    request: () => {
      const patientRemoteId = this.patientRemoteId();
      const treatment = this.treatment.value();
      const data = this.data();

      // Pull medical notes only after we have a treatment id.
      if (!treatment) {
        return undefined;
      }

      // Pull medical notes from PNE only if patient has remote id.
      if (!patientRemoteId) {
        return undefined;
      }

      // Pull medical notes from PNE only if form is empty.
      if (data.length > 0) {
        return undefined;
      }

      return { customerId: patientRemoteId, treatmentId: treatment.id };
    },
    loader: ({ request }) =>
      this.medicalNotesService.getMedicalNotes(request).pipe(
        switchMap((notes) => {
          const baseNotes: readonly MedicalNoteItem[] = notes.map((note) => ({ ...note }));

          if (baseNotes.length === 0) {
            return of(baseNotes);
          }

          const employeeIds = Array.from(
            new Set(baseNotes.map((note) => note.employeeId.trim())),
          ).filter((employeeId) => employeeId.length > 0);

          if (employeeIds.length === 0) {
            return of(baseNotes);
          }

          return forkJoin(
            employeeIds.map((employeeId) =>
              this.medicalNotesService.getEmployee({ employeeId }).pipe(
                map((employee) => ({
                  id: employee.id,
                  name: `${employee.firstName}, ${employee.lastName}`,
                })),
                catchError(() => of({ id: employeeId, name: undefined })),
              ),
            ),
          ).pipe(
            map((employees) => {
              const employeeNames = new Map(
                employees.map((employee) => [employee.id, employee.name]),
              );

              return baseNotes.map((note) => ({
                ...note,
                employeeName: employeeNames.get(note.employeeId),
              }));
            }),
          );
        }),
      ),
  });

  /**
   * Locally created medical notes to be displayed in the table.
   */
  protected readonly localNotes = signal<readonly LocalMedicalNote[]>([]);

  /**
   * Medical notes to be displayed in the table.
   */
  protected readonly notes = computed(() => {
    const localNotes = this.localNotes();
    const existingNotes = this.data();

    if (this.externalNotes.hasValue()) {
      const externalNotes = this.externalNotes.value();

      return [...externalNotes, ...existingNotes, ...localNotes];
    }

    return [...existingNotes, ...localNotes];
  });

  /**
   * Whether to show a message indicating that no data is available.
   */
  protected readonly showNoDataMessage = computed(() => {
    if (!this.externalNotes.hasValue()) {
      return false;
    }

    const externalNotes = this.externalNotes.value();

    return externalNotes.length === 0 && this.data().length === 0 && this.localNotes().length === 0;
  });

  public ngOnInit(): void {
    this.observeNotesChanges();
  }

  protected getNoteMarkerTarget(note: MedicalNoteItem): ScribeSuggestionMarkerTarget {
    const index = this.notes().indexOf(note);
    return this.markerTargets()[index] ?? (note as unknown as ScribeSuggestionMarkerTarget);
  }

  /**
   * Presents the dialog for adding a medical note.
   */
  protected presentDialog(): void {
    this.dialogService
      .prompt(RxDialogComponent, {
        width: '407px',
        component: AddMedicalNoteDialog,
        payload: {
          title: 'Add Medical Note',
          buttons: this.dialogsConfigService.SubmitDismissButtons('Add', 'Cancel'),
          fields: [],
        },
      })
      .pipe(
        filter((result) => typeof result === 'string'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.addNote(result));
  }

  /**
   * Adds a new local medical note.
   */
  protected addNote(text: string): void {
    const user = this.user() ?? this.fallbackUser();

    if (user) {
      this.appendNote(text, user);

      return;
    }

    this.authService
      .whoAmI()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((doctor) => {
        if (!doctor) return;

        const value = new Doctor(doctor);

        this.fallbackUser.set(value);
        this.appendNote(text, value);
      });
  }

  /**
   * Appends note to the `localNotes`.
   */
  private appendNote(text: string, user: unknown): void {
    if (typeof user !== 'object') {
      return;
    }

    if (!user['remote_id'] && !user['id']) {
      return;
    }

    const getEmployeeName = () => {
      if (user['first_name'] && user['last_name']) {
        return `${user['first_name']}, ${user['last_name']}`;
      }

      return undefined;
    };

    this.localNotes.update((value) => [
      ...value,
      {
        date: new Date(),
        employeeId: user['remote_id'] || user['id'].toString(),
        employeeName: getEmployeeName(),
        text: text,
      },
    ]);
  }

  /**
   * Observes `notes` property changes and updates payload.
   */
  protected observeNotesChanges(): void {
    toObservable(this.notes, { injector: this.injector })
      .pipe(skip(1))
      .subscribe((notes) => {
        this.update.emit(
          notes.map((note) => ({
            id: note['id'],
            employeeId: note['employeeId'],
            employeeName: note['employeeName'],
            text: note['text'],
            date: note['date'],
          })),
        );
      });
  }
  /**
   * Index of the note among the widget's stored notes, for the amendment indicator.
   */
  protected getAmendmentRowIndex(note: MedicalNoteItem): number {
    return this.notes().indexOf(note);
  }
}
